import { useState } from 'react';

import { useDebouncedValue } from '@tanstack/react-pacer/debouncer';
import { useQuery } from '@tanstack/react-query';

import { clientSearchOptions } from '@/entities/client';

import { CLIENT_SEARCH_DEBOUNCE_MS } from '../config/schedule-session.config';

// Restated rather than built on `features/client/search-client/model/use-client-search.ts`,
// because a feature may not import another feature's internals.
export function useScheduleSessionClientSearch(enabled: boolean) {
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, { wait: CLIENT_SEARCH_DEBOUNCE_MS });
    const trimmedSearch = debouncedSearch.trim();

    const { data, isFetching } = useQuery(clientSearchOptions(trimmedSearch, enabled));

    const clients = data?.items ?? [];
    // An empty search lists the first page, so no items there means the practice has no clients,
    // not just no matches for a typed search.
    const hasNoClients = trimmedSearch === '' && data !== undefined && clients.length === 0;
    // The debounced term still lags the typed one during the wait, so comparing them tells "still
    // waiting to search" apart from "searched and found nothing".
    const isSearchPending = search.trim() !== trimmedSearch;
    const hasNoResults =
        trimmedSearch !== '' &&
        !isSearchPending &&
        !isFetching &&
        data !== undefined &&
        clients.length === 0;

    return { search, setSearch, clients, isFetching, hasNoClients, hasNoResults };
}
